<script setup lang="ts">
import { EntityAppearanceType } from '@/api/generated/api.enum';
import { Components } from '@/api/generated/api';
import EntityAppearance from '@/core/entity-appearance/EntityAppearance.vue';
import EntityAppearancePicker from '@/core/entity-appearance/EntityAppearancePicker.vue';
import { useSchoolClassAppearance } from '@/composables/useSchoolClassAppearance';
import { useToast } from '@/composables/useToast';
import { appearancesEqual } from '@/core/entity-appearance/appearanceFromDto';
import type { TEntityAppearance } from '@/core/entity-appearance/entityAppearance.types';
import ClassNamesInlineEdit from '@/core/dialogs/create-class/ClassNamesInlineEdit.vue';
import { useSchoolYears } from '@/composables/useSchoolYears';
import { computed, ref, shallowRef, toRefs, useTemplateRef, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useSchoolLevelsBySchoolType } from '@/composables/useSchoolLevelsBySchoolType';
import ArchiveBadge from '@/core/badges/ArchiveBadge.vue';
import FormTextarea from '@/core/forms/FormTextarea.vue';
import { getSchoolYearName } from '@/utils/schoolYear.utils';

interface ISchoolClassGeneralData {
  id?: number;
  tenantId?: number | null;
  schoolYear?: Components.Schemas.BaseSchoolYearDto | null;
  schoolYearId?: number | null;
  schoolLevel: number;
  name: string;
  nickname?: string | null;
  archivedAt?: string | null;
  finalNote?: string | null;
  appearance?: Components.Schemas.EntityAppearanceDto | null;
}

const { t } = useI18n();
const { editSuccessToast } = useToast();
const { allSchoolYearsTenant } = useSchoolYears();
const { getSchoolLevelLabel } = useSchoolLevelsBySchoolType();

const emit = defineEmits<{
  'update:classNames': [value: { name: string; nickname: string }];
  'update:hasError': [value: boolean];
  'update:appearance': [value: TEntityAppearance];
}>();

const props = defineProps<{
  editable?: boolean;
  studentCount?: number;
  schoolClass: ISchoolClassGeneralData | null;
  previewAppearance?: TEntityAppearance;
  deferAppearanceSave?: boolean;
  disabled?: boolean;
}>();
const { schoolClass } = toRefs(props);
const classNicknameInternal = ref<string>('');
const classNameInternal = ref<string>('');
const { appearance, saving, save } = useSchoolClassAppearance(schoolClass);
const appearancePreview = shallowRef<TEntityAppearance>(appearance.value);
const appearancePicker = useTemplateRef('appearancePicker');
const handleAppearanceChange = async (next: TEntityAppearance): Promise<void> => {
  if (props.deferAppearanceSave) {
    emit('update:appearance', next);
    return;
  }
  if (appearancesEqual(appearance.value, next)) return;
  if (await save(next)) editSuccessToast();
};
defineExpose({ closeAppearance: () => appearancePicker.value?.close() });

const schoolYear = computed<string | null>(() => {
  const selectedYear =
    schoolClass.value?.schoolYear || allSchoolYearsTenant.value.find((sy) => sy.id === schoolClass.value?.schoolYearId);
  return selectedYear ? getSchoolYearName(selectedYear, t) : null;
});

const schoolLevelLabel = computed(() => {
  if (schoolClass.value) {
    return getSchoolLevelLabel(schoolClass.value.schoolLevel);
  }
  return '';
});

const handleUpdateClassNames = (value: { name: string; nickname: string }): void => {
  classNameInternal.value = value.name;
  classNicknameInternal.value = value.nickname;
  emit('update:classNames', value);
};

watch(
  schoolClass,
  () => {
    classNicknameInternal.value = schoolClass.value?.nickname || '';
    classNameInternal.value = schoolClass.value?.name || '';
  },
  { immediate: true },
);
</script>

<template>
  <div class="flex flex-col gap-md bg-surface-secondary p-lg rounded-group">
    <div class="flex min-w-0 items-center justify-between gap-md">
      <div class="flex min-w-0 flex-1 items-center gap-md">
        <template v-if="schoolClass">
          <EntityAppearancePicker
            v-if="editable && schoolClass.id != null"
            ref="appearancePicker"
            :key="`${schoolClass.tenantId}:${schoolClass.id}`"
            :model-value="previewAppearance ?? appearance"
            :disabled="saving || disabled"
            :aria-busy="saving"
            :abbreviation="classNameInternal"
            class="shrink-0"
            @preview="appearancePreview = $event"
            @update:model-value="handleAppearanceChange"
          />
          <EntityAppearance
            v-else
            :appearance="previewAppearance ?? appearance"
            :abbreviation="classNameInternal"
            size="lg"
          />
        </template>
        <div v-if="!editable" class="text-title break-words hyphens-auto">
          {{ schoolClass?.nickname || schoolClass?.name }}
        </div>

        <ClassNamesInlineEdit
          v-if="editable"
          :key="`${schoolClass?.tenantId}:${schoolClass?.id}`"
          class="min-w-0 flex-1"
          :name="classNameInternal"
          :nickname="classNicknameInternal"
          :appearance-shows-name="appearancePreview.type === EntityAppearanceType.text"
          @commit="handleUpdateClassNames"
          @update:has-error="emit('update:hasError', $event)"
        />
      </div>
      <ArchiveBadge v-if="schoolClass?.archivedAt" />
    </div>

    <div class="flex flex-col items-start">
      <div class="font-semibold">
        {{ schoolLevelLabel }}
      </div>
      <div>{{ $t('classes.studentCount', { count: studentCount ?? 0 }) }}</div>
      <div>{{ schoolYear }}</div>
    </div>

    <FormTextarea
      v-if="schoolClass?.archivedAt && schoolClass.finalNote"
      name="finalNote"
      :rows="5"
      :fluid="true"
      :readonly="true"
      :value="schoolClass?.finalNote"
      class="min-h-[128px]"
    />
  </div>
</template>
